import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import updateCompanyOptionApi from '@/api/patch/company/company-option/update-company-option';
import Alert from '@/components/alert/alert-component';
import FormWrapper from '@/components/form-wrapper/form-wrapper-component';
import { useMutation } from '@/hooks/use-api';
import getCompanyOptionApi from '@/api/get/company/company-options/get-company-option';
import Loader from '@/components/loader/loader-component';
import OptionsForm from './options-form';
import processOptionData, { processSubmitData } from './options-utils';
import ParentSelectionModal from './parent-selection-modal';
import Styled from './options-new-style';

/**
 * OptionsEdit Component
 *
 * Handles the editing of existing company options with simplified parent selection logic.
 * When historical data is detected (multiple parent_option: null), shows a modal to select parent,
 * then lets processOptionData handle the rest normally.
 *
 * @returns {JSX.Element} The rendered options editing form
 */
const OptionsEdit = () => {
  const navigate = useNavigate();
  const { id, optionId } = useParams();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [showParentModal, setShowParentModal] = useState(false);
  const [historicalOptions, setHistoricalOptions] = useState([]);
  const [selectedParentId, setSelectedParentId] = useState(null);

  const [updateCompanyOption, { loading: updateCompanyOptionLoading }] =
    useMutation(updateCompanyOptionApi);

  /**
   * Fetch initial data on component mount to check for historical issues.
   * If historical issues are found, show the parent selection modal.
   * Otherwise, show the form as usual.
   */
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setInitialLoading(true);
        const response = await getCompanyOptionApi(id, optionId);

        const { prices = [] } = response.data;
        const pricesWithoutParent = prices.filter(
          (price) => price.parent_option === null,
        );

        // Show modal if we have multiple prices without parents
        if (pricesWithoutParent.length > 1) {
          setHistoricalOptions(pricesWithoutParent);
          setShowParentModal(true);
        }
      } finally {
        setInitialLoading(false);
      }
    };

    if (id && optionId) {
      fetchInitialData();
    }
  }, [id, optionId]);

  /**
   * Handles the company option update process
   * @param {object} submitValues - The processed form values to update the company option
   */
  const handleCompanyUpdate = (submitValues) => {
    updateCompanyOption(id, optionId, submitValues).then(() => {
      navigate(`/app/company-management/view/${id}/options`);
      Alert({
        icon: 'success',
        text: 'Option Updated successfully',
      });
    });

    setLoading(false);
  };

  /**
   * Processes and submits form data for update
   * @param {object} values - The form values to be submitted
   */
  const submitForm = async (values) => {
    setLoading(true);
    const submitValues = await processSubmitData(values, 'edit');
    handleCompanyUpdate(submitValues);
  };

  /**
   * Handle parent selection - store selected parent and close modal
   * @param {object} selectedParent - The selected parent option
   */
  const handleParentSelection = (selectedParent) => {
    setSelectedParentId(selectedParent.id);
    setShowParentModal(false);
  };

  const isLoading = updateCompanyOptionLoading || loading;
  const shouldShowForm = !initialLoading && !showParentModal;

  if (initialLoading) {
    return <Loader loading />;
  }

  return (
    <Styled.Main>
      <ParentSelectionModal
        isOpen={showParentModal}
        onClose={() => {
          navigate(`/app/company-management/view/${id}/options`);
        }}
        onConfirm={handleParentSelection}
        options={historicalOptions}
        loading={loading}
      />
      {shouldShowForm && (
        <FormWrapper
          defaultValues={async () =>
            processOptionData(id, optionId, {
              isEdit: true,
              selectedParentId,
            })
          }
          onSubmit={submitForm}
        >
          <OptionsForm
            type="edit"
            isLoading={isLoading}
            submitForm={submitForm}
          />
        </FormWrapper>
      )}
    </Styled.Main>
  );
};

export default OptionsEdit;
